// Resolving the MIME type to stamp on a direct-to-object-storage upload.
//
// The signed PUT carries no content-type binding, so whatever the browser
// sends becomes the object's stored `contentType` — and that stored value is
// what every downstream consumer trusts: the server's accept-time MIME
// allow-lists, the HEIC→JPEG conversion at both accept and serve time, and the
// client's attachment classification.
//
// `file.type` is not trustworthy on its own. Browsers commonly report an EMPTY
// type for `.heic`/`.heif` picks (Android Chrome, Windows Chrome/Firefox, and
// iOS when picking from Files), and Android file providers hand plenty of
// files over as `application/octet-stream`. Upload code that did
// `file.type || "image/jpeg"` therefore stored HEIC bytes labelled as JPEG —
// which silently defeats HEIC conversion and leaves reviewers with an
// undecodable <img> — while `file.type || "application/octet-stream"` stored a
// label the server allow-lists reject, so the file was deleted on submit with
// no error surfaced to anyone.

/**
 * Extension → MIME fallback for picks where the browser reports nothing
 * usable. Deliberately covers the image/video/document types the various
 * server-side allow-lists accept; anything else falls through to the caller's
 * own fallback.
 */
export const EXT_MIME: Record<string, string> = Object.assign(Object.create(null) as Record<string, string>, {
  heic: "image/heic",
  heif: "image/heif",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
  gif: "image/gif",
  mp4: "video/mp4",
  mov: "video/quicktime",
  webm: "video/webm",
  mkv: "video/x-matroska",
  "3gp": "video/3gpp",
  pdf: "application/pdf",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ppt: "application/vnd.ms-powerpoint",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  rtf: "application/rtf",
  zip: "application/zip",
  txt: "text/plain",
  csv: "text/csv",
  // Null-prototype: a file literally named "x.constructor" would otherwise
  // resolve EXT_MIME["constructor"] to the Object FUNCTION — a non-string the
  // type system cannot catch, shipped straight into a Content-Type header.
});

/**
 * Best MIME type to stamp on an upload, in priority order:
 *   1. a HEIC/HEIF file extension — wins outright, because that is exactly the
 *      case browsers mislabel and the one with the worst downstream failure;
 *   2. the browser-reported type, when it is present and not the useless
 *      `application/octet-stream`;
 *   3. the extension map;
 *   4. the caller's field-appropriate fallback.
 */
export function uploadMimeType(
  file: { name?: string; type?: string },
  fallback = "application/octet-stream",
): string {
  const ext = (file.name ?? "").toLowerCase().match(/\.([a-z0-9]+)$/)?.[1];
  const extMime = ext ? EXT_MIME[ext] : undefined;
  if (extMime && (ext === "heic" || ext === "heif")) return extMime;
  const reported = file.type && file.type !== "application/octet-stream" ? file.type : "";
  return reported || extMime || fallback;
}

/**
 * True when the file looks like a HEIC/HEIF pick, by reported type OR by
 * extension. Use for client-side allow-list checks (`ALLOWED.has(file.type)`)
 * that would otherwise reject a HEIC whose reported type is the empty string.
 */
export function isHeicFile(file: { name?: string; type?: string }): boolean {
  const t = (file.type ?? "").toLowerCase();
  if (t === "image/heic" || t === "image/heif") return true;
  const ext = (file.name ?? "").toLowerCase().match(/\.([a-z0-9]+)$/)?.[1];
  return ext === "heic" || ext === "heif";
}
